/**
 * Every brokerage C-BROKER-SEARCH holds, one page at a time: search by MC or name, filter by status, sort by what changed
 * or what is known. A row opens the brokerage's own page. The state lives in the URL, so a view can be shared.
 */

import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import IconButton from '@mui/joy/IconButton'
import Input from '@mui/joy/Input'
import Link from '@mui/joy/Link'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import { BROKER_SEARCH_LIST_SORTS, type BrokerSearchListSort, type BrokerSearchStatus } from '@numeo/types-core'
import * as React from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import { brokerPipelineApi } from 'api/brokerPipeline'
import { ErrorState, Loading, formatNumber, useLoadPipelineData } from '../../load-pipeline/components/shared'
import { brokeragePath, statusColor, when } from '../components/shared'

const PAGE_SIZE = 50
const STATUSES: readonly BrokerSearchStatus[] = ['VERIFIED', 'INACTIVE', 'SUPPRESSED']
const SORT_LABELS: Record<BrokerSearchListSort, string> = {
    updated: 'Last updated',
    registered: 'Registered',
    observed: 'Most observed',
    lastObserved: 'Last observed',
    mc: 'MC',
    name: 'Legal name',
}

export default function BrokeragesTab() {
    const [params, setParams] = useSearchParams()
    const q = params.get('q') ?? ''
    const status = (params.get('status') ?? '') as BrokerSearchStatus | ''
    const sort = (BROKER_SEARCH_LIST_SORTS as readonly string[]).includes(params.get('sort') ?? '') ? (params.get('sort') as BrokerSearchListSort) : 'updated'
    const dir = params.get('dir') === 'asc' ? 'asc' : 'desc'
    const page = Math.max(0, Number(params.get('page') ?? 0) || 0)
    const [draft, setDraft] = React.useState(q)

    /** Changes one or more of the view's settings; any change but the page starts over at the first page. */
    const update = (next: Record<string, string>) => {
        const merged: Record<string, string> = { q, status, sort, dir, page: String(page), ...next }
        if (!('page' in next)) merged.page = '0'
        // Empty settings and the first page stay out of the URL.
        setParams(Object.fromEntries(Object.entries(merged).filter(([key, v]) => v !== '' && !(key === 'page' && v === '0'))))
    }

    const { data, error, loading } = useLoadPipelineData(
        () => brokerPipelineApi.listBrokerages({ q: q || undefined, status: status || undefined, sort, dir, page, limit: PAGE_SIZE }),
        [q, status, sort, dir, page]
    )
    // Paging reaches `depth` rows; past it, a narrower search or another sort finds the rest.
    const pages = data ? Math.max(1, Math.ceil(Math.min(data.total, data.depth) / data.limit)) : 1

    return (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                <Box
                    component="form"
                    sx={{ display: 'flex', gap: 1, flex: '1 1 320px' }}
                    onSubmit={(e) => {
                        e.preventDefault()
                        update({ q: draft.trim() })
                    }}
                >
                    <Input size="sm" placeholder="MC or name, from its start" value={draft} onChange={(e) => setDraft(e.target.value)} sx={{ flex: 1 }} />
                    <Button size="sm" type="submit">
                        Search
                    </Button>
                </Box>
                <Select size="sm" value={status} onChange={(_, v) => update({ status: v ?? '' })} sx={{ minWidth: 150 }}>
                    <Option value="">Every status</Option>
                    {STATUSES.map((s) => (
                        <Option key={s} value={s}>
                            {s}
                        </Option>
                    ))}
                </Select>
                <Select size="sm" value={sort} onChange={(_, v) => update({ sort: v ?? 'updated' })} sx={{ minWidth: 170 }}>
                    {BROKER_SEARCH_LIST_SORTS.map((s) => (
                        <Option key={s} value={s}>
                            {SORT_LABELS[s]}
                        </Option>
                    ))}
                </Select>
                <IconButton
                    size="sm"
                    variant="outlined"
                    title={dir === 'desc' ? 'Descending' : 'Ascending'}
                    onClick={() => update({ dir: dir === 'desc' ? 'asc' : 'desc' })}
                >
                    {dir === 'desc' ? '↓' : '↑'}
                </IconButton>
            </Box>

            {error ? <ErrorState error={error} /> : null}
            {loading && !data ? <Loading /> : null}
            {data ? (
                <>
                    <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 1 }}>
                        {formatNumber(data.total)} brokerage{data.total === 1 ? '' : 's'}
                        {q ? ` matching “${q}”` : ''}
                        {status ? ` · ${status}` : ''} · page {data.page + 1} of {formatNumber(pages)}
                        {data.total > data.depth ? ` (paging reaches the first ${formatNumber(data.depth)}: search or sort to find the rest)` : ''}
                    </Typography>
                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                        <Table size="sm" stripe="odd" hoverRow>
                            <thead>
                                <tr>
                                    <th style={{ width: 90 }}>MC</th>
                                    <th>Legal name</th>
                                    <th style={{ width: 110 }}>Status</th>
                                    <th style={{ width: 100 }}>Authority</th>
                                    <th>Where</th>
                                    <th>Domains</th>
                                    <th style={{ width: 80 }}>Contacts</th>
                                    <th style={{ width: 90 }}>Observed</th>
                                    <th>Last observed</th>
                                    <th>Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((row) => (
                                    <tr key={row.mc}>
                                        <td>
                                            <Link component={RouterLink} to={brokeragePath(row.mc)}>
                                                {row.mc}
                                            </Link>
                                        </td>
                                        <td>
                                            <Link component={RouterLink} to={brokeragePath(row.mc)} color="neutral">
                                                {row.legalName ?? '—'}
                                            </Link>
                                        </td>
                                        <td>
                                            <Chip size="sm" variant="soft" color={statusColor(row.status)}>
                                                {row.status}
                                            </Chip>
                                        </td>
                                        <td>{row.authorityStatus ?? '—'}</td>
                                        <td>{[row.city, row.state].filter(Boolean).join(', ') || '—'}</td>
                                        <td>{row.domains.join(', ') || '—'}</td>
                                        <td>{formatNumber(row.contacts)}</td>
                                        <td>{formatNumber(row.observedCount)}</td>
                                        <td>{when(row.lastObservedAt)}</td>
                                        <td>{when(row.updatedAt)}</td>
                                    </tr>
                                ))}
                                {data.items.length === 0 ? (
                                    <tr>
                                        <td colSpan={10}>No brokerage matches.</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </Table>
                    </Sheet>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                        <Button size="sm" variant="plain" disabled={page === 0} onClick={() => update({ page: String(page - 1) })}>
                            Previous
                        </Button>
                        <Button size="sm" variant="plain" disabled={page + 1 >= pages} onClick={() => update({ page: String(page + 1) })}>
                            Next
                        </Button>
                    </Box>
                </>
            ) : null}
        </>
    )
}
